import throttle from '../../../assets/js/base/_throttle.js';

class ScrHero {

	constructor( node ) {
		this.node = node;
		this.index = -1;
		this.throttle = 20;

		this.classNames = {
			item           : 'scr-hero__item',
			currentItem    : 'scr-hero__item--current',
			stickyItem     : 'scr-hero__item--sticky',
			content        : 'scr-hero__content',
			bars           : 'scr-hero__bars',
			stickyBars     : 'scr-hero__bars--sticky',
			indicator      : 'scr-hero__indicator',
			indicatorSticky: 'scr-hero__indicator--sticky',
			indicatorUp    : 'scr-hero__indicator--up',
			indicatorDown  : 'scr-hero__indicator--down',
			bar            : 'scr-hero__bar',
			currentBar     : 'scr-hero__bar--current',
			cutsTheMustard : 'scr-hero--ctm',
			loaded         : 'scr-hero--loaded',
		};

		// create our bars wrapper
		this.bars = document.createElement( 'div' );
		this.bars.className = this.classNames.bars;
		this.node.appendChild( this.bars );

		this.indicator = this.node.querySelector( `.${this.classNames.indicator}` );

		// model out items and their bar indicator
		this.items = Array.from( this.node.querySelectorAll( `.${this.classNames.item}` ) ).map( node => {
			const bar = document.createElement('div');
			bar.className = `${this.classNames.bar} has-${node.dataset.color || 'purple'}-background-color`;
			this.bars.appendChild(bar);

			return {
				node,
				bar,
				content: node.querySelector( `.${this.classNames.content}` ),
			};
		});

		// add a class to enable the scrolling effect
		if ( this.cutsTheMustard() ) {
			this.node.classList.add( this.classNames.cutsTheMustard );
		}

		// see if we're on a different item on load
		this.calculate();

		// re-calculate on scroll - throttle a bit for browser performance
		window.addEventListener( 'scroll', throttle( this.calculate.bind( this ), this.throttle ) );

		// handler to record the last event type - click vs mouse so we can scroll the items correctly on focus
		const recordEventType = e => this.lastEventType = e.type;
		document.addEventListener( 'click', recordEventType )
		document.addEventListener( 'keydown', recordEventType )

		// scroll the window on link focus if applicable
		const onFocus = (e) => {
			// body is active - nope
			if ( document.activeElement === document.body ) {
				return;
			}

			// last event was not a key event - nope
			if ( ! this.lastEventType || ! this.lastEventType.match( /^key/ ) ) {
				return;
			}

			// HTMLElement.closest is in the site polyfill file
			const parentItemNode = document.activeElement.closest( `.${this.classNames.item}` );

			// activeElement is outside the component - nope
			if ( ! parentItemNode ) {
				return;
			}

			// calculate the new scroll position on item focus
			const rect = parentItemNode.getBoundingClientRect();

			// ie 11 doesn't do scrollX/scrollY
			const scrollX = window.scrollX || window.pageXOffset;
			const scrollY = window.scrollY || window.pageYOffset;

			// wheeeeee
			window.scrollTo( scrollX, scrollY + rect.top );
		}

		// listen for focus events with capturing
		document.addEventListener( 'focus', onFocus, true );

		// override the IE11 mousewheel event to disable smooth scrolling as it causes massive jitter with fixed positions
		if ( window.matchMedia( 'screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none)' ).matches ) {
			document.body.addEventListener( 'mousewheel', e => {
				e.preventDefault();
				window.scrollTo( window.pageXOffset, window.pageYOffset - e.wheelDelta );
			} );
		}

		this.node.classList.add( this.classNames.loaded );
	}

	/**
	 * preserve the ability to disable the scroll functionality by returning false here
	 *
	 * @returns {boolean}
	 */
	cutsTheMustard() {
		if ( window.matchMedia( 'screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none)' ).matches ) {
			// ie11
			return true;
		}

		return true;
	}

	/**
	 * Calculate the closest item and mark it as active
	 */
	calculate() {
		// target point
		const targetPoint = document.documentElement.clientHeight / 2;

		// mid point of each item
		const points = this.items.map( item => {
			let itemRect = item.node.getBoundingClientRect();
			return itemRect.top + ( itemRect.height / 2 );
		} );

		// get the closest point numerically to the target
		const closestPoint = points.reduce( ( current, point ) => {
			return Math.abs( point - targetPoint ) < Math.abs( current - targetPoint ) ? point : current;
		} );

		// get the item index of the point that is closest
		const closestIndex = points.indexOf( closestPoint );

		// to the closest item
		this.goTo( closestIndex );

		// check for the last item so it appears sticky instead of fixed
		if ( closestIndex === this.items.length - 1 ) {
			const nodeRect = this.node.getBoundingClientRect();
			const itemRect = this.items[ closestIndex ].node.getBoundingClientRect();

			this.indicator.classList.add( this.classNames.indicatorUp );
			this.indicator.classList.remove( this.classNames.indicatorDown );

			// stick the bottom bars to the bottom of the block
			if ( nodeRect.bottom < document.documentElement.clientHeight ) {
				this.bars.classList.add( this.classNames.stickyBars );
				this.indicator.classList.add( this.classNames.indicatorSticky );
			} else {
				this.bars.classList.remove( this.classNames.stickyBars );
				this.indicator.classList.remove( this.classNames.indicatorSticky );
			}

			if ( targetPoint > itemRect.top + ( itemRect.height / 2 ) ) {
				this.items[ this.index ].node.classList.add( this.classNames.stickyItem );
			} else {
				this.items[ this.index ].node.classList.remove( this.classNames.stickyItem );
			}
		} else if ( closestIndex === 0 ) {
			this.indicator.classList.remove( this.classNames.indicatorUp );
			this.indicator.classList.add( this.classNames.indicatorDown );
		} else {
			this.indicator.classList.remove( this.classNames.indicatorUp );
			this.indicator.classList.remove( this.classNames.indicatorDown );
		}
	}

	/**
	 * Mark the item and bar active
	 *
	 * @param {int} index - the numeric index of item to go to within this.items
	 */
	goTo( index ) {
		// nothing to do - move along
		if ( index === this.index ) {
			return;
		}

		// unmark the current item if applicable
		if ( this.items[ this.index ] ) {
			this.items[ this.index ].node.classList.remove( this.classNames.currentItem );
			this.items[ this.index ].bar.classList.remove( this.classNames.currentBar );
		}

		// save our current index for later
		this.index = index;

		// mark the item as active
		this.items[ this.index ].node.classList.add( this.classNames.currentItem );
		this.items[ this.index ].bar.classList.add( this.classNames.currentBar );
	}

}

ScrHero.instances = Array.from( document.querySelectorAll('.scr-hero' ) ).map( node => new ScrHero(node) );

export default ScrHero;
